/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { AxiosError } from 'axios';
import { addApiErrorNotification, addNotifications } from '../../actions/pageActions';
import { notaryAPI } from '../../api/notary';
import { NotificationItem } from '@components/common/Notifications/NotificationItem';
import { TaskCard } from '@components/NotaryTaskList/TaskCard';
import { openJournalEntry, openSigningSession } from '@utils/routing';

export namespace AttendAppointmentTask {
  export interface Props {
    roomId: string;
  }
}

/** @return true if there is pending action and signing view should now be displayed */
const checkPendingActions = async (roomId: string, dispatch: Dispatch): Promise<boolean> => {
  const { unlockedBaseJournalEntryIds, pendingCallRoomIds } = await notaryAPI.rooms.fetchPendingActions();
  const notifications: NotificationItem.PropsWithoutId[] = [];

  if (unlockedBaseJournalEntryIds.length) {
    notifications.push({
      iconType: 'error',
      content: 'You have a pending journal entry. Please lock it before attending another appointment.',
      action: 'Open pending journal entry',
      handleActionClick: () => openJournalEntry(unlockedBaseJournalEntryIds[0]),
      type: 'error',
      shouldAutoRemove: false,
    });
  }

  // note that error is not displayed when there is no pending calls
  if (pendingCallRoomIds.length > 0 && !pendingCallRoomIds.includes(roomId)) {
    notifications.push({
      iconType: 'error',
      content: 'You currently have an appointment in progress. Please end it before attending another one.',
      action: 'Open appointment in progress',
      handleActionClick: () => openSigningSession(pendingCallRoomIds[0]),
      type: 'error',
      shouldAutoRemove: false,
    });
  }

  if (notifications.length > 0) {
    addNotifications(notifications)(dispatch);
    return true;
  } else {
    return false;
  }
};

const attendAppointment = async (roomId: string, dispatch: Dispatch): Promise<void> => {
  try {
    await notaryAPI.rooms.prepareAttendAppointment(roomId);
    const hasPendingAction = await checkPendingActions(roomId, dispatch);
    if (!hasPendingAction) {
      openSigningSession(roomId);
    }
  } catch (error) {
    addApiErrorNotification('Failed to attend appointment', error as AxiosError<{ message?: string }>)(dispatch);
  }
};

export const AttendAppointmentTask: React.FC<AttendAppointmentTask.Props> = ({ roomId }) => {
  const dispatch = useDispatch();

  const handleOpenTask = useCallback(() => attendAppointment(roomId, dispatch), [roomId, dispatch]);

  return (
    <TaskCard
      id="attendAppointment"
      taskName="Attend notarization appointment"
      buttonLabel="Attend"
      onOpenTask={handleOpenTask}
    />
  );
};
